import { useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  LinearProgress,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertContext";
import { SummarizeButton } from "./SummarizeButton";
import { TemplatePickerDialog } from "./TemplatePickerDialog";
import {
  useGetSummary,
  useGenerateSummary,
  useSummaryPolling,
  useTranscribeAndSummarizeFlow,
  type TranscribeAndSummarizePhase,
} from "../../hooks/useSummary";
import { InteractionSummary, SummaryStatus } from "../../types/summary/Summary";
import { type RedactionOptions } from "../../api/transcription";

interface SummaryViewProps {
  customerId: string;
  interactionId: string;
  hasExistingTranscript: boolean;
  className?: string;
}

export function SummaryView({
  customerId,
  interactionId,
  hasExistingTranscript,
  className = "",
}: Readonly<SummaryViewProps>) {
  const { t } = useTranslation(["summary"]);
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { data: summary, refetch } = useGetSummary(customerId, interactionId);
  const showAlert = useAlert();

  const canSummarize =
    (user?.hasPermission("interactions:Replay") ||
      user?.hasPermission("interactions:ReplayRaw")) &&
    user?.hasPermission("summary-templates:Read");
  const generateSummary = useGenerateSummary();
  const [isSummarizePolling, setIsSummarizePolling] = useState(false);

  const { data: polledSummary } = useSummaryPolling(
    customerId,
    interactionId,
    isSummarizePolling,
  );

  const {
    phase,
    error: combinedFlowError,
    start: startCombinedFlow,
    reset,
  } = useTranscribeAndSummarizeFlow(customerId, interactionId);

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const currentSummary = polledSummary ?? summary;

  // Handle polling completion — must be in useEffect, not during render
  const prevPolledStatusRef = useRef<SummaryStatus | undefined>();
  useEffect(() => {
    if (!isSummarizePolling || !polledSummary) return;
    if (prevPolledStatusRef.current === polledSummary.status) return;
    prevPolledStatusRef.current = polledSummary.status;

    if (
      [SummaryStatus.Completed, SummaryStatus.Failed].includes(
        polledSummary.status,
      )
    ) {
      setIsSummarizePolling(false);
      refetch();
    }
  }, [isSummarizePolling, polledSummary, refetch]);

  useEffect(() => {
    if (phase === "completed") {
      reset();
      refetch();
    } else if (phase === "error") {
      showAlert(combinedFlowError?.message ?? t("summary:failed"), "error");
      reset();
    }
  }, [phase, combinedFlowError, reset, refetch, showAlert, t]);

  const handleSummarize = () => {
    setIsPickerOpen(true);
  };

  const handleTemplateSelected = (
    templateId: string,
    redactionOptions?: RedactionOptions,
  ) => {
    setIsPickerOpen(false);
    if (hasExistingTranscript) {
      prevPolledStatusRef.current = undefined;
      queryClient.removeQueries({
        queryKey: ["summary-polling", customerId, interactionId],
      });
      generateSummary.mutate(
        { customerId, interactionId, templateId },
        {
          onSuccess: () => {
            setIsSummarizePolling(true);
          },
          onError: (err) => {
            showAlert(err.message, "error");
          },
        },
      );
    } else {
      startCombinedFlow(redactionOptions, templateId);
    }
  };

  const handleRegenerate = () => {
    setIsPickerOpen(true);
  };

  const picker = (
    <TemplatePickerDialog
      open={isPickerOpen}
      onClose={() => setIsPickerOpen(false)}
      onSelect={handleTemplateSelected}
      customerId={customerId}
      hasExistingTranscript={hasExistingTranscript}
    />
  );

  // Progress during combined flow
  if (phase !== "idle" && phase !== "completed") {
    return (
      <>
        <SummaryProgress phase={phase} className={className} />
        {picker}
      </>
    );
  }

  // Progress during summarize-only
  if (isSummarizePolling || generateSummary.isPending) {
    return (
      <>
        <SummaryProgress phase="summarizing" className={className} />
        {picker}
      </>
    );
  }

  // Completed summary
  if (
    currentSummary?.status === SummaryStatus.Completed &&
    currentSummary.summaryText
  ) {
    return (
      <>
        <CollapsibleSummary
          className={className}
          summary={currentSummary}
          onRegenerate={canSummarize ? handleRegenerate : undefined}
        />
        {picker}
      </>
    );
  }

  // Failed state
  if (currentSummary?.status === SummaryStatus.Failed) {
    return (
      <>
        <Box className={`${className} flex flex-col items-center gap-3 p-4`}>
          <Typography variant="body1" color="error">
            {t("summary:failed")}
            {currentSummary.errorMessage
              ? `: ${currentSummary.errorMessage}`
              : ""}
          </Typography>
          {canSummarize ? (
            <SummarizeButton
              onClick={handleSummarize}
              isPending={false}
              hasTranscript={hasExistingTranscript}
            />
          ) : (
            <Tooltip title={t("summary:noPermission")}>
              <span>
                <SummarizeButton
                  onClick={() => {}}
                  isPending={false}
                  hasTranscript={hasExistingTranscript}
                  disabled
                />
              </span>
            </Tooltip>
          )}
        </Box>
        {picker}
      </>
    );
  }

  // No summary — show button or permission message
  return (
    <>
      <Box
        className={`${className} flex flex-col items-center justify-center p-4`}
      >
        {canSummarize ? (
          <SummarizeButton
            onClick={handleSummarize}
            isPending={false}
            hasTranscript={hasExistingTranscript}
          />
        ) : (
          <Tooltip title={t("summary:noPermission")}>
            <span>
              <SummarizeButton
                onClick={() => {}}
                isPending={false}
                hasTranscript={hasExistingTranscript}
                disabled
              />
            </span>
          </Tooltip>
        )}
      </Box>
      {picker}
    </>
  );
}

function CollapsibleSummary({
  className = "",
  summary,
  onRegenerate,
}: {
  className?: string;
  summary: InteractionSummary;
  onRegenerate?: () => void;
}) {
  const { t } = useTranslation(["summary"]);
  const [expanded, setExpanded] = useState(false);

  return (
    <Box
      className={`${className} flex flex-col`}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
          px: 2,
          py: 1,
          backgroundColor: "grey.50",
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon fontSize="small" sx={{ color: "#6366f1" }} />
          <Typography variant="subtitle2" fontWeight={600}>
            {t("summary:title", "Summary")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("summary:generatedAt")}{" "}
            {new Date(summary.updatedAt).toLocaleString()}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {onRegenerate && (
            <Tooltip title={t("summary:regenerate")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRegenerate();
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {expanded ? (
            <ExpandLessIcon fontSize="small" color="action" />
          ) : (
            <ExpandMoreIcon fontSize="small" color="action" />
          )}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <Box
          sx={{
            p: 2,
            "& h1, & h2, & h3": {
              mt: 3.5,
              mb: 1,
              fontWeight: 700,
              fontSize: "1.05rem",
              borderBottom: "1px solid",
              borderColor: "divider",
              pb: 0.5,
            },
            "& h1:first-child, & h2:first-child, & h3:first-child": {
              mt: 0,
            },
            "& h4, & h5, & h6": {
              mt: 2,
              mb: 0.5,
              fontWeight: 600,
              fontSize: "0.95rem",
            },
            "& hr": { display: "none" },
            "& ul, & ol": { pl: 2 },
            "& p": { mt: 0, mb: 1 },
            "& p:last-child": { mb: 0 },
            "& h1 + *, & h2 + *, & h3 + *, & h4 + *, & h5 + *, & h6 + *": {
              mt: 0,
            },
            "& blockquote": {
              borderLeft: "3px solid",
              borderColor: "divider",
              pl: 2,
              ml: 0,
              color: "text.secondary",
            },
          }}
        >
          <Markdown>{summary.summaryText!}</Markdown>
        </Box>
      </Collapse>
    </Box>
  );
}

function SummaryProgress({
  phase,
  className = "",
}: {
  phase: TranscribeAndSummarizePhase;
  className?: string;
}) {
  const { t } = useTranslation(["summary"]);

  const message =
    phase === "transcribing"
      ? t("summary:transcribing")
      : phase === "summarizing"
        ? t("summary:summarizing")
        : "";

  return (
    <Box className={`${className} flex flex-col items-center gap-2 p-4`}>
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    </Box>
  );
}
